// The console sidebar: its sections, and who sees each item.
//
// Kept apart from the shell so the role mapping can be tested on its own. The
// roles mirror the server's checks (lib/access.ts); hiding an item is a
// convenience, not the boundary.

import type * as React from "react";
import {
  Activity,
  Binary,
  Bot,
  Boxes,
  Building2,
  Cpu,
  Database,
  FolderTree,
  Github,
  HeartHandshake,
  KeyRound,
  Layers,
  LayoutDashboard,
  MessagesSquare,
  ScrollText,
  ShieldCheck,
  ThumbsUp,
  Users,
  Wrench,
} from "lucide-react";

import { BUILDERS, ORG_ADMIN } from "@/lib/access";
import type { AdminRole } from "@/types/api";

export interface NavItem {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  roles: AdminRole[];
}

export interface NavSection {
  /** Heading; omitted for the top-level items (Dashboard). */
  title?: string;
  /** Folded away by default; opens when the current page is inside it. */
  collapsible?: boolean;
  items: NavItem[];
}

// Sections follow what an organisation does with Aviary: staff it (agents),
// feed it (knowledge), power it (models), check it (governance), use it.
// The roles mirror the server's checks; see lib/access.ts.
const NAV: NavSection[] = [
  {
    items: [
      {
        href: "/dashboard",
        label: "Dashboard",
        icon: LayoutDashboard,
        roles: ["superadmin", ...BUILDERS],
      },
    ],
  },
  {
    title: "Platform",
    items: [
      { href: "/tenants", label: "Tenants", icon: Building2, roles: ["superadmin"] },
      { href: "/model-catalog", label: "Model catalog", icon: Boxes, roles: ["superadmin"] },
    ],
  },
  {
    title: "Workforce",
    items: [
      // Testing an agent lives on the agent itself (its Test tab).
      { href: "/agents", label: "Agents", icon: Bot, roles: BUILDERS },
      // Engineers can read the code of conduct; only org admins save it.
      {
        href: "/agent-behaviour",
        label: "Agent behaviour",
        icon: HeartHandshake,
        roles: BUILDERS,
      },
      { href: "/domains", label: "Domains", icon: FolderTree, roles: BUILDERS },
      { href: "/capabilities", label: "Tools", icon: Wrench, roles: BUILDERS },
      // Grants outside apps access to the runtime API, so org admins only.
      {
        href: "/trusted-systems",
        label: "Connected systems",
        icon: KeyRound,
        roles: ORG_ADMIN,
      },
    ],
  },
  {
    title: "Knowledge",
    items: [
      { href: "/knowledge-bases", label: "Knowledge base", icon: Database, roles: BUILDERS },
      { href: "/vector-stores", label: "Vector stores", icon: Layers, roles: ORG_ADMIN },
    ],
  },
  {
    title: "Models",
    items: [
      { href: "/llm-models", label: "LLM Providers", icon: Cpu, roles: ORG_ADMIN },
      { href: "/embedding-models", label: "Embedding models", icon: Binary, roles: ORG_ADMIN },
    ],
  },
  {
    title: "Governance",
    items: [
      { href: "/evaluations", label: "Evaluations", icon: ThumbsUp, roles: BUILDERS },
      { href: "/audit-logs", label: "Audit logs", icon: ScrollText, roles: BUILDERS },
      // Holds the org's tracing API key, so org admins only.
      { href: "/observability", label: "Observability", icon: Activity, roles: ORG_ADMIN },
      {
        href: "/governance/security",
        label: "Repo Governance",
        icon: ShieldCheck,
        roles: BUILDERS,
      },
    ],
  },
  {
    title: "Workspace",
    items: [
      // An org admin's view of everyone's chats is the "All" filter inside Chat.
      { href: "/chat", label: "Chat", icon: MessagesSquare, roles: BUILDERS },
      { href: "/users", label: "Users", icon: Users, roles: ORG_ADMIN },
    ],
  },
  {
    title: "Settings",
    collapsible: true,
    items: [{ href: "/github", label: "GitHub", icon: Github, roles: ORG_ADMIN }],
  },
];

/** The sections a role sees, dropping any its role leaves empty. */
export function navFor(role: AdminRole): NavSection[] {
  return NAV.map((section) => ({
    ...section,
    items: section.items.filter((n) => n.roles.includes(role)),
  })).filter((section) => section.items.length > 0);
}

export function isActive(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}
